import React from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import {
  Button,
  ButtonGroup,
  Unstable_Grid2 as Grid,
  Typography,
} from '@mui/material'
import { Apps, Menu } from '@mui/icons-material'

import ProductPriceAvailabilityTable from '@src/components/ProductInfo/ProductPriceAvailabilityTable'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import { ViewPreference } from '@src/routes/SearchResults/SearchResults'

import ViewProductOptionsSelectableChips from './ViewProductOptionsSelectableChips'

const useStyles = makeStyles()((theme) => ({
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '100%',
    justifyContent: 'space-between',
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  toggleLabel: {
    fontSize: theme.typography.pxToRem(12),
    marginRight: theme.spacing(2),
  },
  viewToggleButton: {
    borderRadius: '2px',
    padding: '4px',
    minWidth: '30px',
    border: `1px solid ${theme.palette.grey[400]}`,
  },
}))

interface ViewProductOptionsDesktopViewProps {
  materialPricing: ValidMaterialPricingDetailFragment[] | undefined
  pricingLoading: boolean
  selectedMaterial: ValidMaterialPricingDetailFragment | null
  setSelectedMaterial: (material: ValidMaterialPricingDetailFragment) => void
  handleSelect: (materialNumber: string) => void
  view: ViewPreference | null
  handleSelectView: (view: ViewPreference) => void
}

const ViewProductOptionsDesktopView: React.FC<
  ViewProductOptionsDesktopViewProps
> = ({
  materialPricing,
  pricingLoading,
  selectedMaterial,
  setSelectedMaterial,
  handleSelect,
  view,
  handleSelectView,
}) => {
  const { classes } = useStyles()

  return (
    <div>
      {/* View Toggle */}
      <Grid md={8} className={classes.titleSection}>
        <Typography variant="h3">
          <FormattedMessage id="SELECT_A_SIZE" defaultMessage="Select a Size" />
        </Typography>
        <Grid md={4} className={classes.toggleContainer}>
          <Typography component="span" className={classes.toggleLabel}>
            <FormattedMessage id="CHANGE_VIEW" defaultMessage="Change View" />
          </Typography>
          <ButtonGroup size="small">
            <Button
              variant={view === ViewPreference.grid ? 'contained' : undefined}
              color="primary"
              onClick={() => handleSelectView(ViewPreference.grid)}
              className={classes.viewToggleButton}
            >
              <Apps />
            </Button>
            <Button
              variant={view === ViewPreference.list ? 'contained' : undefined}
              color="primary"
              onClick={() => handleSelectView(ViewPreference.list)}
              className={classes.viewToggleButton}
            >
              <Menu />
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
      {/* Product Views */}
      {view === ViewPreference.grid ? (
        <ViewProductOptionsSelectableChips
          items={
            materialPricing?.map((product) => ({
              packSize: product?.packageSize?.trim() || product?.materialNumber,
              value: product?.materialNumber,
              price: product?.price ?? 0,
              currency: product?.currency,
              availabilities: product?.availabilities,
              hidePriceMessageKey: product?.hidePriceMessageKey,
            })) ?? []
          }
          selectedChip={selectedMaterial?.materialNumber ?? ''}
          loading={pricingLoading}
          loadCount={4}
          onSelect={handleSelect}
        />
      ) : (
        <ProductPriceAvailabilityTable
          loading={pricingLoading}
          materialPricing={materialPricing}
          onRowClick={(row) => {
            setSelectedMaterial(row)
          }}
          rowSelected={(row) =>
            row?.materialNumber === selectedMaterial?.materialNumber
          }
        />
      )}
    </div>
  )
}

export default ViewProductOptionsDesktopView
